import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { put, takeLatest, select, all, take, call } from 'redux-saga/effects'
import { updateUserSignature } from '@tabeeb/modules/account/actions'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { ContentFormRecordType } from '@tabeeb/enums'
import { getIsContentReadonly } from '@tabeeb/modules/readonlyWhiteboard/selectors'
import {
  getReadonlyContentFormRequest,
  getReadonlyFormAnswersByTabRequest,
  ReadonlyWhiteboardKeys,
} from '@tabeeb/modules/readonlyWhiteboard/requests'
import { queryClient } from '@tabeeb/services/queryClient'
import { getReadonlyContentFormSuccess, getReadonlyFormAnswerByTabSuccess } from '@tabeeb/shared/content/actions'
import * as rawActions from '../actions'
import { getCurrentForm } from '../selectors'
import { startLoadingFormAnswers } from '../actions'

function* setCurrentTabs(action) {
  const { payload } = action
  const form = yield select(getCurrentForm)
  const selectedTabs = payload || form.Tabspanels.map((panel) => panel.Tabs[0].Id)

  const isContentReadonly = yield select(getIsContentReadonly)
  if (isContentReadonly) {
    yield put(rawActions.setReadonlySelectedTabs(selectedTabs))

    const pathname = yield select((state) => state.router.location.pathname)
    const tokenMatch = pathname.match(/^\/content\/shared\/([^/]+)/)
    const token = tokenMatch ? tokenMatch[1] : null
    if (token != null && selectedTabs.length > 0 && form.Id > 0) {
      try {
        const data = yield call([queryClient, queryClient.fetchQuery], {
          queryKey: [
            ReadonlyWhiteboardKeys.readonlyContent,
            { Token: token, ContentFormId: form.Id, TabIds: selectedTabs },
          ],
          queryFn: getReadonlyFormAnswersByTabRequest,
        })

        yield put(getReadonlyFormAnswerByTabSuccess({ LatestFormAnswers: data }))
      } catch (e) {
        yield put(onAddErrorNotification({ message: 'Form not found' }))
        return
      }
    }
  } else {
    yield put(rawActions.setSelectedTabs(selectedTabs))
  }
}

function* openForm(action) {
  const contentFormId = action.payload
  yield put(rawActions.setCurrentForm({}))
  yield put(rawActions.getFormControls.request({ contentFormId }))
}

function* onOpenReadonlyForm(action) {
  const contentFormId = action.payload
  yield put(rawActions.setCurrentForm({}))

  const pathname = yield select((state) => state.router.location.pathname)
  const tokenMatch = pathname.match(/^\/content\/shared\/([^/]+)/)
  const token = tokenMatch ? tokenMatch[1] : null

  yield put(startLoadingFormAnswers())
  try {
    const data = yield call([queryClient, queryClient.fetchQuery], {
      queryKey: [ReadonlyWhiteboardKeys.readonlyContent, { Token: token, ContentFormId: contentFormId }],
      queryFn: getReadonlyContentFormRequest,
    })

    yield put(getReadonlyContentFormSuccess(data))
  } catch (e) {
    yield put(onAddErrorNotification({ message: 'Form not found' }))
    return
  }
}

function* saveFormRecord() {
  const form = yield select(getCurrentForm)
  const currentUserId = yield select(getCurrentUserId)

  const data = {
    contentFormId: form.Id,
    userId: currentUserId,
    type: ContentFormRecordType.Manual,
  }

  yield put(rawActions.saveFormRecordRequest(data))
}

function* createUserSignature({ payload }) {
  const form = yield select(getCurrentForm)
  const currentUserId = yield select(getCurrentUserId)

  yield put(updateUserSignature.request(payload))
  yield take(updateUserSignature.success)

  const data = {
    contentFormId: form.Id,
    userId: currentUserId,
    type: ContentFormRecordType.UserSignature,
  }

  yield put(rawActions.saveFormRecordRequest(data))
}

function* signForm() {
  const form = yield select(getCurrentForm)
  const currentUserId = yield select(getCurrentUserId)

  const data = {
    contentFormId: form.Id,
    userId: currentUserId,
    type: ContentFormRecordType.UserSignature,
  }

  yield put(rawActions.saveFormRecordRequest(data))
}

function* saveFormRecordSuccess() {
  yield put(onAddSuccessNotification({ message: 'The form record created successfully' }))
}

function* updateUserSignatureSuccess() {
  yield put(onAddSuccessNotification({ message: 'The signature updated successfully' }))
}

function* currentForm() {
  yield all([
    takeLatest(rawActions.openForm, openForm),
    takeLatest(rawActions.openReadonlyForm, onOpenReadonlyForm),
    takeLatest(rawActions.setCurrentTabs, setCurrentTabs),
    takeLatest(rawActions.saveFormRecord, saveFormRecord),
    takeLatest(rawActions.createUserSignature, createUserSignature),
    takeLatest(rawActions.signForm, signForm),
    takeLatest(rawActions.saveFormRecordSuccess, saveFormRecordSuccess),
    takeLatest(updateUserSignature.success, updateUserSignatureSuccess),
  ])
}

export default currentForm
